import { ChangeDetectionStrategy, Component, computed, ElementRef, inject, ViewEncapsulation } from '@angular/core';
import { IOnboardingBuilderSharingSettingsStep, IOnboardingEditItem } from '../../fi-onboarding';
import { FiComponentsContainer } from '@fi-shared/fi-components-container';
import {
    FiOnboardingBuilderSharingSettingsStep,
    FiOnboardingBuilderStoreService,
    FiOnboardingBuilderTabIndex,
} from '../../fi-onboarding-builder-store.service';
import { FiOnboardingBuilderHelperService } from '../../fi-onboarding-builder-helper.service';
import { FiOnboardingCategoryType } from '@fi-app/fi-onboarding/fi-onboarding-enums';
import { IOnboardingActivateSettings } from './fi-activate-settings/fi-activate-settings.component';

@Component({
    selector: 'fi-settings-sharing-tab',
    templateUrl: './fi-settings-sharing-tab.component.html',
    styleUrls: ['./fi-settings-sharing-tab.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-settings-sharing-tab' },
    standalone: false,
    providers: [{ provide: FiComponentsContainer, useExisting: FiSettingsSharingTabComponent }],
})
export class FiSettingsSharingTabComponent extends FiComponentsContainer {
    private readonly fiOnboardingBuilderHelperService = inject(FiOnboardingBuilderHelperService);
    private readonly fiOnboardingBuilderStoreService = inject(FiOnboardingBuilderStoreService);
    protected readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

    protected onboarding = computed<IOnboardingEditItem>(() =>
        this.fiOnboardingBuilderStoreService.currentOnboarding(),
    );

    protected onboardingActivateSettings = computed<IOnboardingActivateSettings>(() => {
        return {
            DisplayType: this.onboarding()?.DisplayType,
            IsActive: this.onboarding()?.IsActive,
        };
    });
    protected onboardingCategory = computed<FiOnboardingCategoryType>(() => this.onboarding()?.Category);

    protected selectedTabIndex = computed<FiOnboardingBuilderTabIndex>(() => {
        return this.fiOnboardingBuilderStoreService.selectedTabIndex();
    });

    protected currentSettingsStep = computed<FiOnboardingBuilderSharingSettingsStep>(() => {
        return this.fiOnboardingBuilderStoreService.selectedSettingsStep();
    });
    protected isCurrentTabSettings = computed<boolean>(() => {
        return this.selectedTabIndex() === FiOnboardingBuilderTabIndex.SettingsSharing;
    });
    private readonly isStepHiddenFactory = (step: FiOnboardingBuilderSharingSettingsStep) => {
        return computed(() => {
            return !this.isCurrentTabSettings() || this.currentSettingsStep() !== step;
        });
    };
    protected readonly isSharingSettingsStepHidden = this.isStepHiddenFactory(
        FiOnboardingBuilderSharingSettingsStep.Share,
    );
    protected readonly isCategorySettingsStepHidden = this.isStepHiddenFactory(
        FiOnboardingBuilderSharingSettingsStep.Category,
    );
    protected readonly isActivateSettingsStepHidden = this.isStepHiddenFactory(
        FiOnboardingBuilderSharingSettingsStep.Activate,
    );

    protected onCategoryChange(newCategory: FiOnboardingCategoryType): void {
        this.fiOnboardingBuilderStoreService.updateCurrentOnboarding((currentValue) => {
            return {
                ...currentValue,
                Category: newCategory,
                IsActive: newCategory === FiOnboardingCategoryType.SyncReady ? false : currentValue.IsActive,
            };
        });
    }

    protected onActivateSettingsChange(newActivateSetttings: IOnboardingActivateSettings): void {
        this.fiOnboardingBuilderStoreService.updateCurrentOnboarding((currentValue) => {
            return {
                ...currentValue,
                DisplayType: newActivateSetttings.DisplayType,
                IsActive: newActivateSetttings.IsActive,
            };
        });
    }

    protected onSettingsStepChange(step: FiOnboardingBuilderSharingSettingsStep): void {
        this.fiOnboardingBuilderStoreService.setSelectedSettingsStep(step);
    }

    protected readonly settingsSteps: IOnboardingBuilderSharingSettingsStep[] =
        this.fiOnboardingBuilderHelperService.getAvailableSettingsSteps();
}
